import { Component, h, Host, Prop, State } from '@stencil/core';
import { store } from '@stencil/redux';
import { Unsubscribe } from 'redux';
import { closeReservation, openCheckout } from '../../../redux/actions';
import { RootState } from '../../../redux/reducers';
import { Cart, CartItem, ConnectedArticle, ItemDetails, UnitConfig } from '../../../redux/types';
import { postItemDetails } from '../../../api';
import {
  RentwareCheckoutTemplate,
} from '../../rentware-checkout/rentware-checkout-modal/template/rentware-checkout-template';
import { PickerDate } from '../../rtr-article-details/utils/types';
import { PriceCalculation } from '../../../types/types';
import { makeTimePicker } from '../../rentware-article-booking/utils/utils';

@Component({
  tag: 'rtr-cart-reservation',
  styleUrl: 'style.sass',
  shadow: false,
})
export class RentwareCartControlButton {
  @Prop({ mutable: true }) itemId: string;
  @Prop({ mutable: true }) pickerDate: PickerDate;
  @Prop({ mutable: true }) cart: Cart;
  @Prop({ mutable: true }) loadedComponents: { [name: string]: boolean };
  @Prop({ mutable: true }) unitConfig: UnitConfig;

  @State() itemDetails: ItemDetails;

  closeReservation!: typeof closeReservation;
  openCheckout!: typeof openCheckout;
  unsubscribe!: Unsubscribe;

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      const orderId = state.bookingDetails.orderDetails?.id;
      const itemId = state.app.showReservationForId;

      if (orderId && state.app.locale && itemId) {
        postItemDetails(itemId, orderId, state.app.locale, null, false, data => (this.itemDetails = data), undefined);
      }

      return {
        itemId: state.app.showReservationForId,
        cart: state.bookingDetails.cart,
        loadedComponents: state.app.loadedComponents,
        unitConfig: state.system.unitsConfig,
        pickerDate: {
          locale: state.app.locale,
          pickUpDate: state.bookingDetails.fromDate,
          pickUpTime: state.bookingDetails.fromTime,
          dropOffDate: state.bookingDetails.toDate,
          dropOffTime: state.bookingDetails.toTime,
          withTime: state.widget.timePickerEnabled,
          withDateRange: state.widget.kind !== 'date',
          suspectedDropOffDate: null,
          isOrder: true,
          timeSlotPicker: makeTimePicker(state?.widget?.timePicker)
        },
      };
    });

    store.mapDispatchToProps(this, {
      closeReservation,
      openCheckout,
    });
  }

  handleOpenCheckout = (): void => {
    this.openCheckout();

    const intervalId = setInterval(() => {
      const checkoutLoaded = !!this.loadedComponents[RentwareCheckoutTemplate.name];

      if (!checkoutLoaded) {
        return;
      }

      this.closeReservation();
      clearInterval(intervalId);
    }, 100);
  };

  get cartItem(): CartItem {
    const cartItem = this.cart?.items.find(item => item.itemId === this.itemId && !item.isMileageOption);
    const cartItemMileage = this.cart?.items.find(item => item.itemId === this.itemId && item.isMileageOption);
    if (cartItemMileage && cartItemMileage.mileageOption) {
      if (!cartItemMileage.mileageOption.price.priceCalculation) {
        cartItemMileage.mileageOption.price.priceCalculation = PriceCalculation.Interval
      }
      if (!cartItemMileage.mileageOption.relativePrice.price.priceCalculation) {
        cartItemMileage.mileageOption.relativePrice.price.priceCalculation = cartItemMileage.mileageOption.relativePrice.calculationType
      }
    }


    if (!cartItem) return null;

    cartItem.mileageOption = cartItemMileage?.mileageOption;
    if (!cartItem.individualDiscountedPrice.priceCalculation) {
      cartItem.individualDiscountedPrice.priceCalculation = cartItem.priceCalculationType
    }
    if (!cartItem.discountedPrice.priceCalculation) {
      cartItem.discountedPrice.priceCalculation = PriceCalculation.Interval
    }


    return cartItem;
  }

  get connectedArticles(): ConnectedArticle[] {
    const cartItems: CartItem[] = this.cart.items;

    let connectedArticles: ConnectedArticle[] = [];
    cartItems.forEach((cartItem: CartItem) => {
      cartItem.parentIds.forEach(parentId => {
        if (parentId === this.itemDetails.orderItemId) {
          const indexOfDuplicateArticle = connectedArticles.findIndex(connectedArticle => connectedArticle.id === parentId);
          if (indexOfDuplicateArticle === -1) {
            connectedArticles.push({
              ...cartItem,
              connectedAmount: 1,
            });
          } else {
            const duplicateArticle = connectedArticles[indexOfDuplicateArticle];
            duplicateArticle.connectedAmount += 1;
            connectedArticles[indexOfDuplicateArticle] = duplicateArticle;
          }
        }
      });
    });

    // multiply of article number
    connectedArticles = connectedArticles.map(connectedArticle => {
      return {
        ...connectedArticle,
        connectedAmount: connectedArticle.connectedAmount * this.cartItem.qty,
      };
    });

    return connectedArticles;
  }

  render() {
    if (!this.itemDetails || !this.cartItem) {
      return null;
    }

    return (
      <Host>
        <div class="CRWrapper">
          <rtr-cart-reservation-header onCloseButton={this.closeReservation} />

          <rtr-cart-reservation-content
            cartItem={this.cartItem}
            connectedArticles={this.connectedArticles}
            images={this.itemDetails.images}
            onShowFullGallery={this.closeReservation}
            totalPriceCalculation={this.cart.totalPriceCalculation}
          />

          <rtr-cart-reservation-summary onOpenCheckout={this.handleOpenCheckout} pickerDate={this.pickerDate} />
        </div>

        <div class="CRModal" onClick={this.closeReservation} />
      </Host>
    );
  }
}
